import { useLayoutEffect, useRef } from 'react'
import { useSetAtom, type PrimitiveAtom } from 'jotai'

/** A value held by exactly one component instance at a time. */
export interface Claim<T> {
  owner: object
  value: T
}

/**
 * Holds `value` in a single-slot atom for as long as this component instance
 * has one, and releases only its own claim. Two instances can be mounted
 * mid-swap (a view dissolving while the next one enters), so a claimant that
 * goes idle must not clear a slot another instance now holds.
 *
 * Falsy values release the slot.
 */
export function useClaimAtom<T>(claimAtom: PrimitiveAtom<Claim<T> | null>, value: T | null | undefined | false): void {
  const owner = useRef({})
  const setClaim = useSetAtom(claimAtom)

  useLayoutEffect(() => {
    const me = owner.current
    const releaseOwn = () => setClaim((current) => (current?.owner === me ? null : current))
    if (value) {
      setClaim({ owner: me, value })
    } else {
      releaseOwn()
    }
    return releaseOwn
  }, [value, setClaim])
}
